/**
 * SecureApiClient - 認證透明 API 層
 *
 * 核心理念：讓認證問題對業務邏輯完全透明
 * - 自動處理 token 刷新
 * - 智能重試機制
 * - 熔斷器保護
 * - 錯誤分類和恢復
 */

import { httpsCallable, HttpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase';
import { logInfo, logWarn, logError } from '../utils/logger';

// 認證狀態類型
type AuthState =
  | 'authenticated'
  | 'token-refreshing'
  | 'token-expired'
  | 'network-error'
  | 'requires-reauth'
  | 'logged-out';

// 錯誤恢復結果
interface RecoveryResult {
  success: boolean;
  retry: boolean;
  error?: string;
}

// 熔斷器狀態
type CircuitState = 'closed' | 'open' | 'half-open';

// 熔斷器配置
interface CircuitBreakerConfig {
  failureThreshold: number;
  timeout: number;
  monitoringPeriod: number;
}

/**
 * 熔斷器 - 防止系統過載
 */
class AuthCircuitBreaker {
  private failures = 0;
  private lastFailTime = 0;
  private state: CircuitState = 'closed';
  private config: CircuitBreakerConfig;

  constructor(config: CircuitBreakerConfig = {
    failureThreshold: 5,
    timeout: 30000, // 30秒
    monitoringPeriod: 60000 // 1分鐘
  }) {
    this.config = config;
  }

  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      if (Date.now() - this.lastFailTime > this.config.timeout) {
        this.state = 'half-open';
        logInfo('AuthCircuitBreaker', '熔斷器進入半開狀態，嘗試恢復');
      } else {
        throw new Error('Circuit breaker is open - too many auth failures');
      }
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess(): void {
    this.failures = 0;
    if (this.state === 'half-open') {
      this.state = 'closed';
      logInfo('AuthCircuitBreaker', '熔斷器恢復正常狀態');
    }
  }

  private onFailure(): void {
    this.failures++;
    this.lastFailTime = Date.now();

    if (this.failures >= this.config.failureThreshold) {
      this.state = 'open';
      logWarn('AuthCircuitBreaker', '熔斷器開啟 - 認證失敗次數過多', {
        failures: this.failures,
        threshold: this.config.failureThreshold
      });
    }
  }

  getState(): CircuitState {
    return this.state;
  }

  reset(): void {
    this.state = 'closed';
    this.failures = 0;
    this.lastFailTime = 0;
    logInfo('AuthCircuitBreaker', '熔斷器已重置');
  }
}

/**
 * 認證狀態機 - 確保可預測的狀態轉換
 */
class AuthStateMachine {
  private currentState: AuthState = 'authenticated';
  private stateHistory: { state: AuthState; timestamp: number }[] = [];

  getCurrentState(): AuthState {
    return this.currentState;
  }

  transition(newState: AuthState, reason?: string): void {
    const previousState = this.currentState;
    this.currentState = newState;

    // 記錄狀態歷史
    this.stateHistory.push({
      state: newState,
      timestamp: Date.now()
    });

    // 保留最近 10 個狀態
    if (this.stateHistory.length > 10) {
      this.stateHistory = this.stateHistory.slice(-10);
    }

    logInfo('AuthStateMachine', '認證狀態轉換', {
      from: previousState,
      to: newState,
      reason
    });
  }

  getStateHistory(): { state: AuthState; timestamp: number }[] {
    return [...this.stateHistory];
  }

  reset(): void {
    this.currentState = 'authenticated';
    this.stateHistory = [];
    logInfo('AuthStateMachine', '認證狀態機已重置');
  }
}

/**
 * 錯誤恢復策略
 */
class AuthRecoveryStrategy {
  private static pendingRefreshes = new Map<string, Promise<void>>();

  /**
   * 檢查是否為認證錯誤
   */
  static isAuthError(error: any): boolean {
    if (!error) return false;

    const errorMessage = typeof error.message === 'string' ? error.message.toLowerCase() : '';
    const errorCode = typeof error.code === 'string' ? error.code.toLowerCase() : '';

    return (
      errorMessage.includes('permission') ||
      errorMessage.includes('unauthorized') ||
      errorMessage.includes('insufficient') ||
      errorMessage.includes('missing') ||
      errorMessage.includes('unauthenticated') ||
      errorCode.includes('permission') ||
      errorCode.includes('auth') ||
      errorCode.includes('unauthorized') ||
      errorCode.includes('unauthenticated')
    );
  }

  /**
   * 執行認證恢復
   */
  static async recover(): Promise<RecoveryResult> {
    try {
      if (!auth.currentUser) {
        return {
          success: false,
          retry: false,
          error: 'No authenticated user'
        };
      }

      const userId = auth.currentUser.uid;

      // 防止並發刷新
      if (this.pendingRefreshes.has(userId)) {
        await this.pendingRefreshes.get(userId);
        return { success: true, retry: true };
      }

      const refreshPromise = this.doRefresh();
      this.pendingRefreshes.set(userId, refreshPromise);

      try {
        await refreshPromise;
        logInfo('AuthRecoveryStrategy', '認證恢復成功');
        return { success: true, retry: true };
      } finally {
        this.pendingRefreshes.delete(userId);
      }

    } catch (error) {
      logWarn('AuthRecoveryStrategy', '認證恢復失敗', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      return {
        success: false,
        retry: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * 執行實際的 token 刷新
   */
  private static async doRefresh(): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('No authenticated user for refresh');
    }

    // 強制從服務器獲取新 token
    await auth.currentUser.getIdToken(true);

    // 重新載入用戶資料
    await auth.currentUser.reload();

    // 小延遲確保狀態同步
    await new Promise(resolve => setTimeout(resolve, 500));
  }
}

/**
 * SecureApiClient - 主要的認證透明 API 客戶端
 */
export class SecureApiClient {
  private circuitBreaker: AuthCircuitBreaker;
  private stateMachine: AuthStateMachine;
  private functionCache = new Map<string, HttpsCallable>();

  constructor() {
    this.circuitBreaker = new AuthCircuitBreaker();
    this.stateMachine = new AuthStateMachine();
  }

  /**
   * 透明的 API 調用 - 自動處理所有認證問題
   */
  async call<T>(functionName: string, data?: any): Promise<T> {
    return this.circuitBreaker.execute(async () => {
      return this.executeWithRetry(functionName, data);
    });
  }

  /**
   * 帶重試的執行邏輯
   */
  private async executeWithRetry<T>(functionName: string, data?: any, maxRetries = 2): Promise<T> {
    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // 預防性檢查 - 在關鍵操作前刷新即將過期的 token
        if (attempt === 0) {
          await this.preventiveRefresh();
        }

        // 執行實際的 API 調用
        const fn = this.getFunction(functionName);
        const result = await fn(data);

        // 成功時更新狀態
        this.stateMachine.transition('authenticated', 'API call successful');

        return (result.data as T);

      } catch (error) {
        lastError = error;

        logWarn('SecureApiClient', `API 調用失敗 (嘗試 ${attempt + 1}/${maxRetries + 1})`, {
          functionName,
          error: error instanceof Error ? error.message : 'Unknown error'
        });

        // 檢查是否為認證錯誤
        if (AuthRecoveryStrategy.isAuthError(error)) {
          this.stateMachine.transition('token-expired', 'Auth error detected');

          // 最後一次嘗試時不再重試
          if (attempt === maxRetries) {
            this.stateMachine.transition('requires-reauth', 'Max retries exceeded');
            break;
          }

          // 嘗試恢復認證
          this.stateMachine.transition('token-refreshing', 'Starting recovery');
          const recovery = await AuthRecoveryStrategy.recover();

          if (recovery.success && recovery.retry) {
            this.stateMachine.transition('authenticated', 'Recovery successful');
            // 繼續重試
            continue;
          } else {
            this.stateMachine.transition('requires-reauth', 'Recovery failed');
            break;
          }
        } else {
          // 非認證錯誤，不重試
          break;
        }
      }
    }

    // 所有重試都失敗
    logError('SecureApiClient', 'API 調用最終失敗', {
      functionName,
      error: lastError instanceof Error ? lastError.message : 'Unknown error',
      state: this.stateMachine.getCurrentState()
    });

    throw lastError;
  }

  /**
   * 預防性 token 刷新
   */
  private async preventiveRefresh(): Promise<void> {
    try {
      if (!auth.currentUser) return;

      // 獲取當前 token 並檢查是否即將過期（5分鐘內）
      const token = await auth.currentUser.getIdTokenResult();
      const expirationTime = new Date(token.expirationTime).getTime();
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      if (expirationTime - now < fiveMinutes) {
        logInfo('SecureApiClient', 'Token 即將過期，執行預防性刷新');
        await auth.currentUser.getIdToken(true);
      }
    } catch (error) {
      // 預防性刷新失敗不應該阻止原始操作
      logWarn('SecureApiClient', '預防性 token 刷新失敗，繼續執行原始操作', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  /**
   * 獲取或創建 Firebase Function 實例
   */
  private getFunction(name: string): HttpsCallable {
    if (!this.functionCache.has(name)) {
      this.functionCache.set(name, httpsCallable(functions, name));
    }
    return this.functionCache.get(name)!;
  }

  /**
   * 獲取當前認證狀態
   */
  getAuthState(): AuthState {
    return this.stateMachine.getCurrentState();
  }

  /**
   * 獲取熔斷器狀態
   */
  getCircuitState(): CircuitState {
    return this.circuitBreaker.getState();
  }

  /**
   * 重置所有狀態（主要用於測試）
   */
  reset(): void {
    this.circuitBreaker.reset();
    this.stateMachine.reset();
    this.functionCache.clear();
    logInfo('SecureApiClient', '所有狀態已重置');
  }

  /**
   * 獲取診斷資訊
   */
  getDiagnostics() {
    return {
      authState: this.stateMachine.getCurrentState(),
      circuitState: this.circuitBreaker.getState(),
      stateHistory: this.stateMachine.getStateHistory(),
      cachedFunctions: Array.from(this.functionCache.keys())
    };
  }
}

// 導出單例實例
export const secureApiClient = new SecureApiClient();

// 導出簡化的調用方法
export const secureCall = secureApiClient.call.bind(secureApiClient);